import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, STATUS_LABELS, TASK_STATUSES } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, GripVertical, Plus, Trash2, FileDown, Save, Loader2, Type } from "lucide-react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function FormEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [project, setProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    api.get(`/forms/${id}`).then((r) => {
      setForm(r.data);
      api.get(`/projects/${r.data.project_id}`).then((rp) => setProject(rp.data));
    });
  }, [id]);

  if (!form) return <div className="text-slate-500">Chargement…</div>;

  const updateField = (k, v) => setForm({ ...form, [k]: v });
  const updateSection = (sid, k, v) => {
    setForm({ ...form, sections: form.sections.map((s) => s.id === sid ? { ...s, [k]: v } : s) });
  };
  const addSection = () => {
    const newSection = { id: crypto.randomUUID(), title: "Nouvelle section", order: form.sections.length, type: "text", content: "" };
    setForm({ ...form, sections: [...form.sections, newSection] });
  };
  const removeSection = (sid) => setForm({ ...form, sections: form.sections.filter((s) => s.id !== sid) });

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = form.sections.findIndex((s) => s.id === active.id);
    const newIdx = form.sections.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(form.sections, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
    setForm({ ...form, sections: reordered });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, sections: form.sections.map((s, i) => ({ ...s, order: i })) };
      const r = await api.put(`/forms/${id}`, payload);
      setForm(r.data);
      toast.success("Enregistré");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const exportWord = async () => {
    await save();
    try {
      const res = await api.get(`/forms/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${form.name}.docx`; a.click();
      toast.success("Export Word généré");
    } catch { toast.error("Erreur export"); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => project && navigate(`/projects/${project.id}`)} className="text-sm text-slate-600 hover:text-btp-navy flex items-center gap-1">
        <ArrowLeft size={14} /> {project ? "Retour au projet" : "Retour"}
      </button>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Nom du livrable</div>
            <input value={form.name} onChange={(e) => updateField("name", e.target.value)} className="w-full text-2xl font-heading font-bold tracking-tight border-b-2 border-transparent focus:border-btp-navy focus:outline-none py-1" data-testid="form-name-input" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Type</div>
            <select value={form.doc_type} onChange={(e) => updateField("doc_type", e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white" data-testid="form-type-select">
              <option>TDR</option><option>Proposition</option><option>APS</option><option>APD</option><option>DAO</option><option>Rapport</option>
            </select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Statut</div>
            <select value={form.status} onChange={(e) => updateField("status", e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white" data-testid="form-status-select">
              {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-xs text-slate-500"><StatusBadge status={form.status} /> · {form.sections.length} section(s)</div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="h-9 px-3 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50 flex items-center gap-2" data-testid="form-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
            </button>
            <button onClick={exportWord} className="h-9 px-3 bg-btp-navy hover:bg-btp-navy-hover text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="form-export-btn">
              <FileDown size={14} /> Exporter Word
            </button>
          </div>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={form.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {form.sections.map((s) => (
              <SectionBlock key={s.id} section={s} onUpdate={updateSection} onRemove={removeSection} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button onClick={addSection} className="w-full h-12 border-2 border-dashed border-slate-300 rounded-md text-sm font-medium text-slate-600 hover:border-btp-navy hover:text-btp-navy flex items-center justify-center gap-2" data-testid="add-section-btn">
        <Plus size={14} /> Ajouter une section
      </button>
    </div>
  );
}

function SectionBlock({ section, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-slate-200 rounded-md p-5 editor-block" data-testid={`section-${section.id}`}>
      <div className="flex items-start gap-3">
        <button {...attributes} {...listeners} className="text-slate-300 hover:text-slate-600 cursor-grab pt-2" data-testid={`drag-${section.id}`}>
          <GripVertical size={18} />
        </button>
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <Type size={14} className="text-slate-400" />
            <input value={section.title} onChange={(e) => onUpdate(section.id, "title", e.target.value)}
              className="flex-1 font-heading text-lg font-bold tracking-tight border-b border-transparent focus:border-btp-navy outline-none py-1"
              placeholder="Titre de la section" data-testid={`section-title-${section.id}`} />
          </div>
          <textarea value={section.content} onChange={(e) => onUpdate(section.id, "content", e.target.value)}
            placeholder="Contenu… (sauter une ligne pour nouveau paragraphe)"
            rows={Math.max(4, (section.content?.split("\n").length || 1))}
            className="w-full border border-slate-200 rounded p-3 text-sm leading-relaxed font-sans focus:border-btp-navy outline-none resize-y"
            data-testid={`section-content-${section.id}`} />
        </div>
        <button onClick={() => onRemove(section.id)} className="text-slate-300 hover:text-red-600 pt-2" data-testid={`remove-${section.id}`}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
